import { PDF_TEMPLATE_NAMES, type PdfTemplateName } from '@/lib/pdfTemplates/shared';

/**
 * Modelo de PDF POR ORÇAMENTO (`quotes.pdf_template`) — fonte única do par
 * builder/leitor, no padrão de `quoteTotals`/`quotePaymentInfo`/`quoteWarranty`
 * (armadilhas #69/#70/#73): a gravação espalha `...buildQuotePdfTemplateColumns`
 * e a leitura passa por `quotePdfTemplateFromRow`, porque os payloads de
 * `quotes` são montados por lista explícita em ~12 lugares e esquecer um perde
 * o campo em silêncio.
 *
 * A COLUNA TEM DOIS ESTADOS, e a distinção é a regra de negócio:
 *
 *   `NULL`  → o orçamento SEGUE O PADRÃO da empresa (`companies.pdf_template`).
 *             É o estado de todo orçamento antigo (a coluna nasceu depois) e de
 *             todo orçamento em que o usuário nunca abriu o seletor. Se a
 *             empresa trocar o padrão, o PDF desses documentos acompanha —
 *             exatamente o comportamento que o app sempre teve.
 *   valor   → o usuário ESCOLHEU um modelo para ESTE documento; o PDF fica
 *             preso a ele e não muda quando o padrão da empresa mudar.
 *
 * Ausência não é escolha (irmão do #61/#73): converter `null` em `'classico'`
 * na gravação congelaria todo o histórico no clássico no primeiro save.
 */

/** `''`/desconhecido/nulo → `null` (= padrão da empresa). */
export function normalizeQuotePdfTemplate(value: unknown): PdfTemplateName | null {
  if (typeof value !== 'string') return null;
  return (PDF_TEMPLATE_NAMES as string[]).includes(value)
    ? (value as PdfTemplateName)
    : null;
}

/**
 * Espalhe (`...buildQuotePdfTemplateColumns(data)`) em todo payload de
 * cabeçalho de `quotes` alimentado pelo formulário. Payloads SEM formulário
 * (PDV, import de visitante) gravam `pdf_template: null` explícito — lá o
 * padrão da empresa é o comportamento correto e não há escolha a preservar.
 */
export function buildQuotePdfTemplateColumns(
  data: { pdfTemplate?: PdfTemplateName | string | null } | null | undefined,
): { pdf_template: string | null } {
  return { pdf_template: normalizeQuotePdfTemplate(data?.pdfTemplate) };
}

/** Leitura da linha (servidor OU cache offline) para o estado do formulário. */
export function quotePdfTemplateFromRow(
  row: { pdf_template?: string | null } | null | undefined,
): PdfTemplateName | null {
  return normalizeQuotePdfTemplate(row?.pdf_template);
}

/**
 * O modelo EFETIVO na hora de gerar o PDF: o escolhido no documento, senão o
 * padrão da empresa, senão `classico`. O dispatcher (`resolveTemplate` em
 * `pdfGenerator.ts`) ainda força `classico` para conta grátis — este helper não
 * repete o gate de plano de propósito (uma regra, um lugar).
 */
export function resolveQuotePdfTemplate(
  quoteTemplate: PdfTemplateName | string | null | undefined,
  companyTemplate: PdfTemplateName | string | null | undefined,
): PdfTemplateName {
  return (
    normalizeQuotePdfTemplate(quoteTemplate) ??
    normalizeQuotePdfTemplate(companyTemplate) ??
    'classico'
  );
}
